import React from 'react';
import { FORM_STATUS } from '../types';

const Dashboard = ({ templates, clients, forms, onNavigate, onEditForm, onSyncForm, shareStatusByFormId = {} }) => {
  // Stats
  const totalTemplates = templates.length;
  const totalClients = clients.length;
  const totalForms = forms.length;
  const draftForms = forms.filter(f => f.status === FORM_STATUS.DRAFT).length;
  const completedForms = forms.filter(f => f.status === FORM_STATUS.COMPLETED).length;

  // Recent forms (last 5)
  const recentForms = [...forms]
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 5);

  return (
    <div>
      <div className="card">
        <h1 style={{ textAlign: 'center', color: 'var(--primary)', marginBottom: '30px' }}>
          FQHC Dynamic Forms System
        </h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          Create templates, manage clients, and fill dynamic interview forms
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid-3" style={{ marginBottom: '20px' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', color: 'var(--primary)', marginBottom: '10px' }}>
            {totalTemplates}
          </div>
          <div style={{ color: '#666' }}>Form Templates</div>
          <button 
            className="btn btn-primary btn-sm mt-4"
            onClick={() => onNavigate('templates')}
          >
            Manage Templates
          </button>
        </div>
        
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', color: 'var(--accent)', marginBottom: '10px' }}>
            {totalClients}
          </div>
          <div style={{ color: '#666' }}>Clients / Organizations</div>
          <button 
            className="btn btn-primary btn-sm mt-4"
            onClick={() => onNavigate('clients')}
          >
            Manage Clients
          </button>
        </div>
        
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', color: 'var(--success)', marginBottom: '10px' }}>
            {totalForms}
          </div>
          <div style={{ color: '#666' }}>Total Forms</div>
          <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>
            <span style={{ color: '#856404' }}>{draftForms} Draft</span> | 
            <span style={{ color: '#155724' }}> {completedForms} Completed</span>
          </div>
          <button
            className="btn btn-primary btn-sm mt-4"
            onClick={() => onNavigate('forms')}
          >
            Manage Forms
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="card" style={{ backgroundColor: '#e0f7fa', borderLeft: '4px solid var(--accent)' }}>
        <h2 className="card-title" style={{ borderBottom: 'none', marginBottom: '10px' }}>How It Works</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ 
              width: '30px', 
              height: '30px', 
              borderRadius: '50%', 
              backgroundColor: 'var(--primary)', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>1</div>
            <div>
              <strong>Clients</strong> - Clients are the organizations or individuals you are creating forms for. Add their details to manage and track forms.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ 
              width: '30px', 
              height: '30px', 
              borderRadius: '50%', 
              backgroundColor: 'var(--primary)', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>2</div>
            <div>
              <strong>Templates</strong> - Create form templates with sections and fields that can be reused for different clients. Templates define the structure of your forms.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ 
              width: '30px', 
              height: '30px', 
              borderRadius: '50%', 
              backgroundColor: 'var(--primary)', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>3</div>
            <div>
              <strong>Create Form</strong> - Select a template + client to create a filled form instance. Fill out the form with client-specific information and save progress as needed.
            </div>
          </div>
        </div>
        
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="card-title">Quick Actions</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-primary"
            onClick={() => onNavigate('new-form')}
          >
            + Create New Form (Select Template + Client)
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => onNavigate('clients')}
          >
            + Add New Client
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => onNavigate('templates')}
          >
            + Create New Template
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => onNavigate('forms')}
          >
            Manage Forms
          </button>
        </div>
      </div>

      {/* Templates Section */}
      <div className="card">
        <h2 className="card-title">Available Templates</h2>
        {templates.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
            No templates yet. Create your first template!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {templates.slice(0, 5).map(template => (
              <div 
                key={template.id} 
                className="list-item"
              >
                <div className="list-item-info">
                  <div className="list-item-title">{template.name}</div>
                  <div className="list-item-subtitle">
                    {template.category} | {template.sections?.length || 0} sections | {template.sections?.reduce((acc, s) => acc + s.fields?.length, 0) || 0} fields
                  </div>
                </div>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => onNavigate('new-form', { template })}
                >
                  Use This Template
                </button>
              </div>
            ))}
          </div>
        )}
        {templates.length > 5 && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => onNavigate('templates')}
            >
              View All {templates.length} Templates
            </button>
          </div>
        )}
      </div>

      {/* Recent Forms */}
      <div className="card">
        <h2 className="card-title">Recent Forms</h2>
        {recentForms.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
            No forms yet. Create your first form!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {recentForms.map(form => {
              const template = templates.find(t => t.id === form.templateId);
              const client = clients.find(c => c.id === form.clientId);
              const shareStatus = shareStatusByFormId[form.id];
              const isShared = Boolean(shareStatus?.active || form.activeShareLink);
              
              return (
                <div 
                  key={form.id} 
                  className="list-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onEditForm?.(form)}
                >
                  <div className="list-item-info">
                    <div className="list-item-title">
                      {form.title || template?.name || 'Untitled Form'}
                    </div>
                    <div className="list-item-subtitle">
                      Client: {client?.name || 'Unknown'} | 
                      Template: {template?.name || 'Unknown'} | 
                      <span className={`badge badge-${form.status}`} style={{ marginLeft: '8px' }}>
                        {form.status}
                      </span> | 
                      Updated: {new Date(form.updatedAt).toLocaleDateString()}
                      {isShared && (
                        <>
                          {' '}| <span className="badge badge-shared">Shared link active</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="df-action-group">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSyncForm?.(form);
                      }}
                      disabled={shareStatus?.loading}
                    >
                      {shareStatus?.loading ? 'Syncing...' : 'Sync'}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditForm?.(form);
                      }}
                    >{isShared ? 'View' : 'Edit'}</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Clients Summary */}
      <div className="card">
        <h2 className="card-title">Clients</h2>
        {clients.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
            No clients yet. Add your first client!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {clients.slice(0, 5).map(client => {
              const clientForms = forms.filter(f => f.clientId === client.id);
              return (
                <div 
                  key={client.id} 
                  className="list-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onNavigate('client-forms', { client })}
                >
                  <div className="list-item-info">
                    <div className="list-item-title">{client.name}</div>
                    <div className="list-item-subtitle">
                      Location: {client.location || 'Not specified'} | 
                      Contact: {client.contactName || 'Not specified'} | 
                      Forms: {clientForms.length}
                    </div>
                  </div>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigate('client-forms', { client });
                    }}
                  >
                    View Forms
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {clients.length > 5 && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => onNavigate('clients')}
            >
              View All {clients.length} Clients
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
